import { Typography, TypographyProps } from '@ethings/ui-toolkit';
import { compact } from 'lodash';
import { FC, useMemo } from 'react';
import { useOptionalSearch } from '../../pages/list/use-search';

interface BoldMatchedTextProps {
  text: string;
  query?: string;
  // Back-compat: allow callers to pass legacy MUI-like sx objects.
  // We'll map supported keys (e.g., color) to props and the rest to inline style.
  sx?: Record<string, any>;
  style?: React.CSSProperties;
  variant?: TypographyProps['variant'];
  noWrap?: boolean;
}

export const BoldMatchedText: FC<BoldMatchedTextProps> = ({ text, query, variant, style, sx, noWrap = true }) => {
  const searchContext = useOptionalSearch();
  const searchString = query || searchContext?.text || '';

  const terms = useMemo(() => searchString.trim().split(/\s+/g).filter((t) => t), [searchString]);

  const { splitRegex, regex } = useMemo(() => {
    const escapedTerms = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return {
      splitRegex: new RegExp(`(${escapedTerms.join('|')})`, 'gi'),
      regex: new RegExp(`^(${escapedTerms.join('|')})$`, 'i'),
    };
  }, [terms]);

  if (!text) return <></>;
  if (terms.length === 0) return <>{text}</>;

  const stringArray = compact(text.split(splitRegex));
  const colorProp: string | undefined =
    sx &&
      typeof sx === 'object' &&
      !Array.isArray(sx) &&
      typeof (sx as { color?: unknown }).color === 'string'
      ? (sx as { color?: string }).color
      : undefined;

  return (
    <Typography variant={variant} component="span" noWrap={noWrap}>
      {stringArray.map((matches, i) => (
        <Typography
          component="span"
          variant={variant}
          key={`${matches}-${i}-${matches.length}`}
          fontWeight={regex.test(matches) ? '800' : 'inherit'}
          color={colorProp}
          style={{
            ...(style || {}),
            ...(sx && typeof sx === 'object' && !Array.isArray(sx)
              ? Object.fromEntries(Object.entries(sx).filter(([k]) => k !== 'color'))
              : {}),
          }}
        >
          {matches}
        </Typography>
      ))}
    </Typography>
  );
};
